import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import ms from 'ms';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ItemCardBadge from '@/components/GridItemCard/Card/components/ItemCardBadge/ItemCardBadge';
import ItemCardImage from '@/components/GridItemCard/Card/components/ItemCardImage/ItemCardImage';
import { ItemCardPriceRow } from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import ItemCardMenu from '@/components/ItemCard/components/Menu/ItemCardMenu';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import {
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummaryPromoted,
    getItemSummarySaleStartTs,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { isMyBidsPage, isSavedItemsPage } from '@/utils/urls';

const PastItemCardSmall: ItemCardComponent = ({ className, itemId }) => {
    const formatMessage = useAppFormatMessage();
    const trackItemCardClicked = useTrackItemCardClicked();
    const { pathname } = useLocation();

    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));

    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));

    const [hover, setHover] = useState(false);
    const [linkHover, setLinkHover] = useState(false);
    const isMobile = useIsMobileLayout();
    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = catalogStatus === 'online' && !biddingWithin7Days;

    const isBuyNow = buyNowStatus === 1;

    const onCardClick = useCallback(() => {
        if (isSavedItemsPage(pathname)) {
            trackItemCardClicked({
                catalogId,
                itemId,
                promoted,
                source: ItemCardSource.MyItemsPageFavoritesPast,
            });
        } else if (isMyBidsPage(pathname)) {
            trackItemCardClicked({
                catalogId,
                itemId,
                promoted,
                source: ItemCardSource.MyItemsPageBidsPast,
            });
        }
    }, [catalogId, itemId, pathname, promoted, trackItemCardClicked]);

    const enterHoverState = useCallback(() => {
        setHover(true);
    }, []);

    const leaveHoverState = useCallback(() => {
        setHover(false);
    }, []);

    const enterLinkHoverState = useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHover(false);
    }, []);

    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;
    const auctionDateInfo = isBuyNow ? (
        <BodySecondary color="secondary">{formatMessage(['buy_now'])}</BodySecondary>
    ) : (
        <ItemCardShortDate
            allowBidding
            itemId={itemId}
            showDateWithoutYear={showDateWithoutYear}
            showOnlyYear={isSold || isPassed || isPending}
        />
    );
    return (
        <article
            className="relative"
            data-testid={`saved-item-card-${itemId}`}
            onClick={onCardClick}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
        >
            <section
                className="mx-auto"
                data-testid="expansion-container"
            >
                <section
                    className={cn(
                        "max-[375px]:[grid-template-rows:116px] max-[375px]:[grid-template-areas:'itemimage_alldetailsformobilelayout'] grid w-[inherit] [grid-template-columns:1fr] [grid-template-rows:min-content_min-content_min-content] justify-center [grid-template-areas:'itemimage'_'.'_'alldetailsformobilelayout'] xsMax:[grid-template-columns:1fr_4fr] xsMax:gap-x-sm xsMax:[grid-template-areas:'itemimage_alldetailsformobilelayout']",
                        className
                    )}
                    data-testid="saved-items-card-grid"
                >
                    <div
                        className="relative aspect-square"
                        data-testid="image-container"
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                    >
                        {!isMobile && (
                            <ItemCardBadge
                                itemId={itemId}
                                savedItemsCard
                            />
                        )}
                        <span className="absolute top-xs right-xs z-[2] xsMax:top-2xs xsMax:right-[5px]">
                            <ItemCardSaveIcon itemId={itemId} />
                        </span>
                        <div className="[grid-area:itemimage]">
                            <ItemCardImage
                                itemId={itemId}
                                shouldGiveRangeForMobileSize={isMobile}
                                showBidStatus
                                useInFluidGrid
                            />
                        </div>
                    </div>
                    <div className="grid [grid-template-rows:6px_22px_min-content_4px] overflow-hidden [grid-area:alldetailsformobilelayout] [grid-template-areas:'.'_'iteminfo'_'title'_'.'_'currentbidprice'] xsMax:[grid-template-rows:24px_min-content_4px] xsMax:[grid-template-areas:'iteminfo'_'title'_'.'_'currentbidprice']">
                        <div className="flex min-w-0 justify-between [grid-area:iteminfo]">
                            <span className="w-full leading-[18px]">{auctionDateInfo}</span>
                        </div>
                        <div className="[grid-area:title]">
                            <ItemCardTitle
                                isHovered={linkHover}
                                itemId={itemId}
                                onMouseEnter={enterLinkHoverState}
                                onMouseLeave={leaveLinkHoverState}
                                showLotNumber
                                testid="itemCardTitle"
                            />
                        </div>
                        <div className="flex justify-between [grid-area:currentbidprice]">
                            <div className="flex items-center">
                                <ItemCardPriceRow
                                    isHovered={linkHover}
                                    itemId={itemId}
                                    onMouseEnter={enterLinkHoverState}
                                    onMouseLeave={leaveLinkHoverState}
                                />
                            </div>
                            {isLoggedIn && (
                                <ItemCardMenu
                                    itemId={itemId}
                                    showIcon={hover}
                                />
                            )}
                        </div>
                    </div>
                </section>
            </section>
        </article>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: PastItemCardSmall,
};

export default info;
